import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const MAX_SECONDS = 60;
const pickType = () => ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/aac"].find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";

export function useVoiceInput(onText) {
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const [seconds, setSeconds] = useState(0);
  const rec = useRef(null);
  const timer = useRef(null);

  useEffect(() => () => { clearInterval(timer.current); rec.current?.stream.getTracks().forEach((t) => t.stop()); }, []);

  const stop = () => { clearInterval(timer.current); if (rec.current?.state === "recording") rec.current.stop(); };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) return toast.error("Voice input isn't supported in this browser");
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
    catch { return toast.error("Allow microphone access to ask by voice"); }
    const type = pickType();
    const r = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
    const chunks = [];
    r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    r.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      const blob = new Blob(chunks, { type: r.mimeType || type || "audio/webm" });
      if (blob.size < 1000) { setState("idle"); return toast.error("Didn't catch that — hold on a bit longer"); }
      setState("transcribing");
      try {
        const form = new FormData();
        form.append("file", new File([blob], `q.${blob.type.includes("mp4") || blob.type.includes("aac") ? "mp4" : "webm"}`, { type: blob.type }));
        const { data } = await api.post("/transcribe", form, { headers: { "Content-Type": "multipart/form-data" } });
        if (data.text) onText(data.text); else toast.error("Didn't catch any words — try again");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't transcribe");
      } finally {
        setState("idle");
      }
    };
    rec.current = r;
    r.start();
    setSeconds(0);
    setState("recording");
    timer.current = setInterval(() => setSeconds((s) => { if (s + 1 >= MAX_SECONDS) stop(); return s + 1; }), 1000);
  };

  return { state, seconds, start, stop };
}
